// @flow

import StateMachine from 'javascript-state-machine';
import type { ReceiverUiTransition } from 'Common/cast/types';
import { TransitionsQueue } from './TransitionsQueue';
import { TRANSITIONS, STATE_TRANSITION_MAPPING } from './CastReceiverStateTransitions';
import { type ReceiverUiState } from './ReceiverUiStates';
import { CastReceiver } from '.';

type StateMachineT<T, S> = {|
  is: (isItCurrentState: T) => boolean,
  can: (canSwitchTo: T) => void,
  cannot: (cannotSwitchTo: T) => void,
  transitions: () => Array<T>,
  allTransitions: () => Array<T>,
  allStates: () => Array<S>,
  state: S,
  [key: T]: (payload: ?Object) => void,
|};

type StateTransitionInfo = {|
  transition: ReceiverUiTransition,
  from: ReceiverUiState,
  to: ReceiverUiState,
|};

class ReceiverStateMachine {
  _sm: StateMachineT<ReceiverUiTransition, ReceiverUiState>;
  _lastTransition: ?ReceiverUiTransition;
  _onStateChange: (state: ReceiverUiState) => void;
  _transitionsQueue: TransitionsQueue;

  constructor(onStateChange: (state: ReceiverUiState) => void) {
    this._onStateChange = onStateChange;
    this._sm = new StateMachine({
      init: 'idle',
      transitions: TRANSITIONS,
      methods: { onEnterState: this._onEnterState },
    });
    this._transitionsQueue = new TransitionsQueue(this._goTo);

    if (process.env.IN_DEBUG) window.stateMachine = this._sm;
  }

  handleEvent(type: string, isAustinEvent: boolean) {
    if (type === 'PLAYER_LOAD_COMPLETE' && isAustinEvent === true) {
      CastReceiver.log(`[CAST][STATE MACHINE] Will not handle PLAYER_LOAD_COMPLETE for Austin.`);
      return;
    }

    const transition: ?ReceiverUiTransition = STATE_TRANSITION_MAPPING.get(type);

    if (transition) {
      if (['pauseAustin', 'pauseBitmovin', 'stream', 'play', 'austinSeek'].includes(transition)) {
        /* These events should affect UI immediately, while all others are throttled to prevent flickering of ui */
        this._transitionsQueue.add({ transition, releaseInMs: 0 });
      } else {
        this._transitionsQueue.add({ transition });
      }

      if (type === 'ERROR' || type === 'onError') {
        this._scheduleIdleTransition();
      }
    }
  }

  _scheduleIdleTransition = () =>
    this._transitionsQueue.add({ transition: 'idle', releaseInMs: 5000 });

  _goTo = (transition: ReceiverUiTransition) => {
    if (this._lastTransition === transition) {
      CastReceiver.log(
        `[CAST][STATE MACHINE] Will not perform ${transition} - it has been already applied.`,
      );
      return;
    }

    if (this._sm.can(transition)) {
      this._logCurrentState();
      this._sm[transition]();
      this._lastTransition = transition;
    } else {
      this._logFailedTransition(transition);
    }
  };

  _logCurrentState = () => {
    const availableTransitions = this._getAvailableTransitions().join(', ');
    CastReceiver.log(
      `[CAST][STATE MACHINE] CurrentState "${this._sm.state}". ` +
        `Available transitions: ${availableTransitions}`,
    );
  };

  _getAvailableTransitions = () => this._sm.transitions();

  _logFailedTransition = (to: ReceiverUiTransition) => {
    const { state } = this._sm;
    const availableTransitions = this._getAvailableTransitions().join(', ');

    console.warn(
      `[CAST][STATE MACHINE] Fail to switch from "${state}", to "${to}" available transitions: ${availableTransitions}`,
    );
  };

  _onEnterState = ({ transition, from, to }: StateTransitionInfo) => {
    CastReceiver.log(
      `[CAST][STATE MACHINE][TRANSITION] "${transition}", from "${from}" to "${to}"`,
    );
    this._onStateChange(to);
  };
}

export { ReceiverStateMachine };
